
export const paths = {
    currentTime: "/currentTime",
    options: "whoo/options",
    optionArray: "/optionArray",
    optionSortMap: "/sortMap",
    header: "/whoo/header",
    /** Index slug -> {ownerId, pollId} fuer Sample Polls (admin-published). */
    samplePollSlugs: "/whoo/samplePollSlugs",
    /**
     * Participant link slugs (concept-custom-participant-links-2026-10-28 §4): {slug} -> {ownerId, pollId, createdAt}.
     * One entry per current slug AND per alias (header voteSlug / voteSlugAliases). Written by the organizer
     * (owner, legacy delegate, ownerKey grant) only when the slug is free; never re-pointed; deleted with the poll.
     */
    pollSlugs: "/whoo/pollSlugs",
    votes: "whoo/votes",
    /**
     * Kontaktfelder der Votes (userEmail, userPhone, privateComment) unter
     * {ownerId}/{pollId}/{voterId} — per Rules nur fuer Poll-Owner (+Delegates)
     * und den Voter selbst lesbar, waehrend whoo/votes public bleibt.
     */
    votePrivate: "whoo/votePrivate",
    /**
     * Oeffentliche Per-Option-Aggregate {ownerId}/{pollId}/{optionIndex} ->
     * {positiveCount, personsSum}, gepflegt vom votesCounter-Trigger (Admin SDK,
     * Rules: read true / write false). Grundlage der Limit-Pruefung fuer
     * Teilnehmer, die bei showParticipants=false die Vote-Liste nicht lesen
     * duerfen. KEIN totalVotes hier — die Gesamt-Anzahl bleibt allein
     * header/votesCounter (analysis-option-counts-2026-08-31).
     */
    voteCounts: "whoo/voteCounts",
    participation: "whoo/participation",
    /**
     * Poll scoped access (concept-poll-scoped-access-2026-10-07 §3, rules since 1.1):
     * {ownerId}/{pollId} -> {ownerKey, editKey, editors/{uid}}. A grant whose key equals ownerKey
     * makes a co-owner, one equal to editKey an editor. No .read/.write on the poll level — keys
     * are read and written per child path only. Keys are crypto.randomUUID(), never overwritten
     * (create via "set if null" transaction, rotate as a separate action).
     */
    pollAccess: "whoo/pollAccess",
    /**
     * Poll grants + My-Polls index in one: {uid}/{ownerId}-{pollId} ->
     * {ownerId, pollId, key, source: 'adopt'|'link'|'mail', grantedAt}. The rules validate key
     * against pollAccess/{o}/{p}/{ownerKey|editKey} on every write.
     */
    pollEditors: "whoo/pollEditors",
    /** Vote scoped access: {ownerId}/{pollId}/{voterId} -> {editKey}; one key, one role (answer editor). */
    voteAccess: "whoo/voteAccess",
    /**
     * Vote grants + My-Votes index: {uid}/{ownerId}-{pollId}-{voterId} ->
     * {ownerId, pollId, voterId, key, source, grantedAt}; key validated against voteAccess editKey.
     */
    voteEditors: "whoo/voteEditors",
    images: "images",
    /**
     * Pending-Eintraege des Code-Logins (Code-Auth P1, concept-passwordless-code-auth-2026-10-22 §3.1):
     * {addressKey} -> {email, codeHash, intent, displayName?, language, requesterUid?, createdAt, expiresAt,
     * attempts, sendCount, windowStart}. addressKey = sha256(lowercase(trim(email))) wie /emailSettings.
     * Ein Eintrag ist KEIN Konto (nichts in Auth, nichts in users/); er entsteht bei `request` und verschwindet
     * bei `confirm`, nach 5 Fehlversuchen oder im taeglichen mailMaintenance (expiresAt < now - 24 h).
     * Unterknoten `_ip/{sha256(ip)}` -> {windowStart, count} ist der IP-Zaehler (20 Requests/Stunde).
     * Functions-owned (Admin SDK) — keine Client-Rule, kein Client-Zugriff.
     */
    authCodes: "/authCodes",
    /**
     * Client-lesbare App-Konfiguration (Code-Auth P3, 2026-10-24). `config/auth/passwordLoginEnabled`: `false`
     * blendet den Passwort-Login im Login-Formular aus (`AuthConfigService`); fehlender Knoten oder Lesefehler
     * zaehlen als `true` (fail open). Rules: `.read: true` auf dem Flag, keine Write-Rule — nur Admin SDK/CLI schreibt
     * (`firebase database:set /config/auth/passwordLoginEnabled false`).
     */
    config: "/config",
    emails: {
        /**
         * E-Mail-Einstellungen pro Adresse (E-Mail-Einstellungen P1, concept-email-settings-2026-10-21 §3.1):
         * {addressKey} -> {email (lowercase), optOut: {confirmation?, updates?, comments?: true}, updatedAt, source}.
         * addressKey = sha256(lowercase(trim(email))) als Hex. Fehlender Knoten oder leeres optOut = alles an.
         * Functions-owned (Admin SDK) — kein Client-Zugriff. Kategorien-Map: categoryOf() in shared/mail/email.ts.
         */
        settings: "/emailSettings",
        toBeProcessed: "/emails/toBeProcessed",
        processed: "/emails/processed",
        unsubscribed: "/emails/unsubscribed",
        error: "/emails/error",
        /**
         * Tagesaggregate {YYYY-MM-DD} -> {processed, errors, retried}. Bleiben dauerhaft
         * stehen — sie sind alles, was von der Fehler-Historie uebrig bleibt, sobald die
         * Retention die Mail-Bodies geloescht hat.
         */
        stats: "/emails/stats",
        /**
         * Zustand des Retry-Laeufers: {circuitOpenRuns} — aufeinanderfolgende Runs,
         * in denen der Circuit-Breaker offen war. Functions-owned (Admin SDK), kein
         * Client-Zugriff.
         */
        runnerState: "/emails/runnerState",
    },
    /**
     * Tagesaggregate {YYYY-MM-DD} -> {accessLinkProcessed, accessLinkErrors,
     * feedbackProcessed, feedbackErrors}. Gegenstueck zu /emails/stats fuer die
     * beiden Notification-Queues, seit die Retention auch deren Buckets raeumt.
     */
    notificationStats: "/notifications/stats",
    accessLinkNotification: {
        toBeProcessed: "/notifications/accessLinkNotification/toBeProcessed",
        processed: "/notifications/accessLinkNotification/processed",
        error: "/notifications/accessLinkNotification/error",
    },
    feedbackNotification: {
        toBeProcessed: "/notifications/feedback/toBeProcessed",
        processed: "/notifications/feedback/processed",
        error: "/notifications/feedback/error",
    },
    /**
     * Rate-Limit-Marker der Private-Comment-Sofortmail:
     * {ownerId}/{pollId}/{voterId} -> {lastSentAt, lastCommentHash, dayKey, dayCount}.
     * Functions-owned (Admin SDK) — kein Client-Zugriff (database.rules.json).
     */
    privateCommentMail: "/notifications/privateCommentMail",
    /**
     * Rate-Limit-Marker der Vote-Bestaetigungs-Mail an Teilnehmer:
     * {ownerId}/{pollId}/{voterId} -> {lastSentAt, lastVoteHash, dayKey, dayCount}.
     * Functions-owned (Admin SDK) — kein Client-Zugriff (database.rules.json).
     */
    voteConfirmationMail: "/notifications/voteConfirmationMail",
    notificationsQueue: "notificationsQueue",
    /**
     * Fehlgeschlagene Vote-Notifications: {yyyy-mm-dd-hh}/{recipientUid}/{pushId}.
     * Datums-Buckets statt flacher Push-IDs — die Namen sortieren aber ebenfalls
     * chronologisch, deshalb funktionieren Digest-Fenster und Retention-Cutoff hier
     * ueber denselben orderByKey()-Mechanismus.
     */
    notificationsQueueError: "notificationsQueue/error",
    /**
     * Flat auto-close index {ownerId}-{pollId} -> {ownerId, pollId, deadlineMs,
     * reminderNotifiedAt?, closedNotifiedAt?}. Maintained by the
     * whocan-maintainAutoCloseIndex trigger, queried by whocan-processPollDeadlines.
     * Functions-owned (Admin SDK) — clients have no access: /whoo/notPublic has no
     * client rules, and /whoo grants .read per public child node, not at the root.
     */
    autoClose: "/whoo/notPublic/autoClose",
    users: "/users",
    feedback: "user-feedback",
    // shop: the Scrumpoker functions (shared functions codebase) use these paths. Whocan-App nutzt diese Pfade seit
    // rules-hardening-1 (2026-10-26) nicht mehr, the rules close /shop for every client
    paymments: "/shop/payments",
    activeProducts: "shop/activeProducts", // the active products assgined to the user
    deactivatedProducts: "shop/deactivatedProducts",
    functions: {
        paypalStart: 'shop-paypalStart',
        paypalProcess: 'shop-paypalProcess',
    }
};

export const storagePaths = {
    users: "users",
    images: "images"
}

